import type { APIStrippedUser, StrippedUser } from 'common/api/endpoints/users';
import type { APIIdeaStatus, IdeaStatus, StatusType } from 'common/api/resources/statuses';
import type { MimeTypeValues } from 'common/constants/files';
import type { FieldDisplayMode, IdeaFieldTypes } from 'common/constants/ideas';
import type { IdeaScoreFieldTypes } from 'common/constants/ideaScoreFields';
import type { Source } from 'common/constants/sources';
import type { CalculationFunctions } from 'common/prioritization/CalculationFactorTypes';
import type { CustomIdeaFieldTypes, Fields } from 'common/types/ideas';
import type { APIGroup } from './group';

export type Idea = {
  _id: string;
  ancestorIDs: string[];
  childCount: number;
  companyID: string;
  created: string;
  deleted: boolean;
  description: string;
  fields: Fields; // default & custom fields
  files: { mimeType: MimeTypeValues; name: string; url: string }[];
  groupID: string | null;
  groupName: string | null;
  groupSortKey: number;
  imageURLs: string[];
  ownerID: string | null;
  parent: Pick<Idea, 'urlName' | 'title'> | null;
  parentID: string | null;
  source: Source;
  sortKeys: Record<string, string | number | Date>;
  statusID: string;
  title: string;
  urlName: string;
  users: Record<string, StrippedUser>;
};

export type APIIdea = {
  id: string;
  childCount: number;
  created: string;
  description: string;
  author: APIStrippedUser | null;
  group: APIGroup | null;
  owner: APIStrippedUser | null;
  parent: Pick<APIIdea, 'id' | 'title' | 'urlName'> | null;
  source: Source;
  status: APIIdeaStatus;
  title: string;
  updatedAt: string;
  urlName: string;
};

export type IdeaIDOnly = Pick<Idea, '_id'>;

export type IdeaLight = {
  _id: string;
  title: string;
  urlName: string;
  statusID: string;
};

interface BaseIdeaField {
  name: string;
  label: string;
  immutable?: boolean;
  deletable?: boolean;
}

export interface DefaultIdeaField extends BaseIdeaField {
  type: Exclude<IdeaFieldTypes, IdeaFieldTypes.formula | IdeaFieldTypes.score>;
  custom: false;
  options?: string[];
  defaultSortKey?: number | string;
}

export interface CustomIdeaField extends BaseIdeaField {
  type: CustomIdeaFieldTypes;
  custom: true;
  options?: string[];
  order: number | null;
  displayMode?: FieldDisplayMode;
}

export interface FormulaIdeaField extends BaseIdeaField {
  type: IdeaFieldTypes.formula;
  custom: true;
  factor: string | null;
  fieldID: string | null;
  segmentID: string | null;
  calculationFunction: CalculationFunctions;
  displayMode?: FieldDisplayMode;
  order: number | null;
}

export interface ScoreIdeaField extends BaseIdeaField {
  type: IdeaFieldTypes.score;
  custom: true;
  fields: Array<{ fieldID: string; weight: number; type: IdeaScoreFieldTypes }>;
  displayMode?: FieldDisplayMode;
  order: number | null;
}

export type IdeaField = DefaultIdeaField | CustomIdeaField | FormulaIdeaField | ScoreIdeaField;

export type IdeaTheme = {
  _id: string;
  autopilotEnabled: boolean;
  created: string; // ISO string
  createdByAutopilot: boolean;
  description: string;
  name: string;
};

type WithID<T extends IdeaField> = {
  _id: string;
} & T;

export type UpdateFormulaFieldResponse = {
  formulaField: FormulaIdeaField;
};

export type UpdateFormulaFieldData = {
  formulaFieldID: string;
  name: string;
  displayMode?: FieldDisplayMode;
};

export type UpdateScoreFieldResponse = {
  ideaScoreField: ScoreIdeaField;
};

export type UpdateScoreFieldData = {
  scoreFieldID: string;
  name: string;
  fields: ScoreIdeaField['fields'];
  displayMode?: FieldDisplayMode;
};

export type UpdateCustomFieldResponse = {
  updatedField: CustomIdeaField;
};

export type UpdateCustomFieldData = {
  customIdeaFieldID: string;
  name: string;
  options?: string[];
  displayMode?: FieldDisplayMode;
};

export type CreateFormulaFieldResponse = {
  formulaField: WithID<FormulaIdeaField>;
};

export type CreateFormulaFieldData = {
  calculationFunction: CalculationFunctions;
  factor: string | null;
  fieldID: string | null;
  name: string;
  segmentID: string | null;
  displayMode?: FieldDisplayMode;
};

export type CreateScoreFieldResponse = {
  ideaScoreField: WithID<ScoreIdeaField>;
};

export type CreateScoreFieldData = {
  name: string;
  fields: ScoreIdeaField['fields'];
  displayMode?: FieldDisplayMode;
};

export type CreateCustomFieldResponse = {
  customIdeaField: WithID<CustomIdeaField>;
};

export type CreateCustomFieldData = {
  name: string;
  type: CustomIdeaFieldTypes;
  options?: string[];
  displayMode?: FieldDisplayMode;
};

// returned by IdeasConfigurationEndpoint
export type IdeasConfiguration = {
  fields: IdeaField[];
  statuses: IdeaStatus[];
  themes: IdeaTheme[];
};

// returned by GetIdeaImpactEndpoint
export type IdeaImpact = {
  name: string;
  value: number;
};

export { StatusType };

// CreateStatusEndpoint, UpdateStatusEndpoint
export type StatusResponse = {
  status: IdeaStatus;
};

// CreateIdeaEndpoint, UpdateIdeaEndpoint
export type IdeaResponse = {
  idea: Idea;
};

export enum AutogroupIdeasStatus {
  notRunning = 'not running',
  running = 'running',
  completed = 'completed',
  failed = 'failed',
}

export type UpdateIdeaFields = Record<string, string | string[] | number | boolean | null | Date>;

export type CreateIdeaData = {
  title: string;
  description?: string;
  groupID?: string | null;
  parentID?: string | null;
  details?: string;
  fields?: UpdateIdeaFields;
};

export type CreateIdeaFromInsightData = {
  insightID: string;
  title: string;
};

// if you update this, also update UpdateIdeaEndpoint
export type UpdateIdeaData = {
  idea: Idea;
  description?: string;
  fields?: UpdateIdeaFields;
  fileURLs?: string;
  imageURLs?: string;
  title?: string;
};

export type MergeIdeaRequest = {
  mergeIdeaID: string;
  mergeIntoIdeaID: string;
};

export type MergeIdeaData = {
  mergeIdea: Idea;
  mergeIntoIdea: Idea;
};

export type UnmergeIdeaData = {
  ideaID: string;
};

export type MoveIdeaData = {
  idea: Idea;
  groupID?: string | null;
  parentID?: string | null;
};

export enum BulkEditIdeaOperations {
  merge = 'merge',
  move = 'move',
  delete = 'delete',
  updateOwner = 'updateOwner',
  updateStatus = 'updateStatus',
}

export type BulkMergeIdeaRequest = {
  mergeIntoIdeaID: string;
};

export type BulkMoveIdeaRequest = {
  groupID: string | null;
  parentID: string | null;
};

export type BulkUpdateOwnerIdeaRequest = {
  ownerID: string | null;
};

export type BulkUpdateStatusIdeaRequest = {
  statusID: string;
};

export type BulkEditIdeaRequest = {
  ideaIDs: string[];
  operationType: BulkEditIdeaOperations;
};

export type BulkMergeData = BulkEditIdeaRequest &
  BulkMergeIdeaRequest & { operationType: BulkEditIdeaOperations.merge };
export type BulkMoveData = BulkEditIdeaRequest &
  BulkMoveIdeaRequest & { operationType: BulkEditIdeaOperations.move };
export type BulkUpdateStatusData = BulkEditIdeaRequest &
  BulkUpdateStatusIdeaRequest & { operationType: BulkEditIdeaOperations.updateStatus };
export type BulkUpdateOwnerData = BulkEditIdeaRequest &
  BulkUpdateOwnerIdeaRequest & { operationType: BulkEditIdeaOperations.updateOwner };
export type BulkDeleteData = BulkEditIdeaRequest & { operationType: BulkEditIdeaOperations.delete };

export type IdeasBetaToggleRequest = {
  enabled: boolean;
  reason?: string;
};

export type AutogroupIdeasStatusResponse = {
  status: AutogroupIdeasStatus;
};

export type ReorderIdeaFieldsRequest<ID = string> = {
  fieldIDs: ID[];
};
